import { useEffect } from 'react';

import { TranslationService } from '@/utils/services/TranslationService';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import useToggle from '@/utils/hooks/utils/useToggle';

import { url_app_android, url_app_ios } from '@/lib/app/urlDownloadApp';

import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import Modal from '@/components/base/Modal';
import { Illustration } from '@/components/composite/Illustration';

import downloadNewAppEn from '../../../../../public/images/illustrations/download_app/download_new_app_en.webp';
import downloadNewAppEs from '../../../../../public/images/illustrations/download_app/download_new_app_es.webp';
import downloadNewAppFr from '../../../../../public/images/illustrations/download_app/download_new_app_fr.webp';

const MODAL_CLOSED_KEY = 'ico-new-mobile-app-modal-closed';

const SchedulerNewMobileAppModal = () => {
    const { isOpen, toggle } = useToggle({ initial: false });
    const { t } = useTranslation('commons');

    useEffect(() => {
        const hasClosedBefore = localStorage.getItem(MODAL_CLOSED_KEY);
        if (!hasClosedBefore) {
            toggle();
        }
    }, []);

    const handleClose = () => {
        localStorage.setItem(MODAL_CLOSED_KEY, 'true');
        toggle();
    };

    const renderBenefits = (icon: string, text: string) => {
        return (
            <span className="border-icoGray-200 flex h-12 w-full items-center gap-2 rounded-md border px-5 py-3 font-GSemiBold text-xs dark:border-icoDarkMode-anthracite md:w-[calc(50%-0.25rem)]">
                <Icon
                    className="flex h-4 w-4 items-center justify-center text-icoBlue"
                    icon={icon}
                />
                {t(text)}
            </span>
        );
    };

    const renderDownloadButton = (icon: string, text: string, link: string) => {
        return (
            <Button
                className="flex w-[50%] items-center gap-3 px-5 py-3 font-GSemiBold text-sm"
                size="lg"
                onClick={() => window.open(link, '_blank')}>
                <Icon icon={icon} />
                {t(text)}
            </Button>
        );
    };

    return (
        <Modal onDismiss={handleClose} open={isOpen}>
            <Modal.Body className="min-h-full w-full rounded-2xl bg-white p-10 dark:bg-icoDarkMode-anthracite">
                <div className="flex flex-col items-center justify-center gap-8">
                    <Illustration
                        width={520}
                        height={200}
                        className="max-w-auto"
                        src={{
                            en: downloadNewAppEn,
                            fr: downloadNewAppFr,
                            es: downloadNewAppEs
                        }}
                    />
                    <div className="flex flex-col items-center justify-center gap-5">
                        <h1 className="text-icoGray-900 dark:text-icoDarkMode-white w-full font-GSemiBold text-xl">
                            {t('new_mobile_app_modal.title')}
                        </h1>
                        <div className="flex flex-col gap-3">
                            <p className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                                {t('new_mobile_app_modal.description')}
                            </p>
                            <div className="flex flex-wrap items-start justify-center gap-2">
                                {renderBenefits(
                                    'calendar-check-outline',
                                    'new_mobile_app_modal.benefits_1'
                                )}
                                {renderBenefits(
                                    'activity-outline',
                                    'new_mobile_app_modal.benefits_2'
                                )}
                                {renderBenefits('pen-outline', 'new_mobile_app_modal.benefits_3')}
                                {renderBenefits(
                                    'folder-file-outline',
                                    'new_mobile_app_modal.benefits_4'
                                )}
                            </div>
                            <div className="mt-7 flex gap-2">
                                {renderDownloadButton(
                                    'apple',
                                    'new_mobile_app_modal.button_download_ios',
                                    url_app_ios
                                )}
                                {renderDownloadButton(
                                    'google-play',
                                    'new_mobile_app_modal.button_download_android',
                                    url_app_android
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export const getStaticProps = TranslationService.getTranslationMessages('scheduler');

export default SchedulerNewMobileAppModal;
